/**
 * routes.jsx — AI CPG Tools routes, ready to drop into App.js.
 *
 * In App.js:
 *   import { AILabRoutes } from "@/pages/ai-lab/routes";
 *   ...
 *   <Routes>
 *     ...existing routes...
 *     {AILabRoutes({ guard: ProtectedRoute })}
 *   </Routes>
 *
 * `guard` is your existing auth wrapper component (it should render <Outlet/> or children when the
 * user is logged in). If your app uses a different pattern, just copy the <Route> list below.
 *
 * Route map
 *   /ai-cpg-tools                          Dashboard (tiles)
 *   /ai-cpg-tools/prompt-builder           Hierarchical picker (category → task)
 *   /ai-cpg-tools/prompt-builder/:taskId   Guided form + generated prompt
 *   /ai-cpg-tools/tools                    AI Tools directory
 *   /ai-cpg-tools/brand-profile            AI Brand Profile (enter once, used everywhere)
 *   /ai-cpg-tools/saved                    My Prompts
 *   /ai-cpg-tools/:categorySlug            Toolkit page (packaging, branding, photography, advertising, video,
 *                                          social, website, retail, investor, research, finance, operations)
 */
import React from "react";
import { Route, Outlet } from "react-router-dom";
import { BrandProfileProvider } from "@/hooks/useBrandProfile";
import AppShell from "@/components/AppShell";
import MenuGate from "@/components/MenuGate";
import AILabDashboard from "./AILabDashboard";
import PromptBuilder from "./PromptBuilder";
import ToolkitPage from "./ToolkitPage";
import AIToolsDirectory from "./AIToolsDirectory";
import BrandProfilePage from "./BrandProfilePage";
import SavedPromptsPage from "./SavedPromptsPage";

function AILabLayout() {
  // Render every AI CPG Tools page inside the app's main <AppShell>
  // (sidebar + header). AILabShell (the sub-header shown at the top of
  // each inner page) is designed to sit *inside* the app's authenticated
  // layout — see its docstring — not replace it.
  //
  // The MenuGate wrapper matches the visibility of the "AI CPG Tools"
  // sidebar entry (`nav-ai-cpg-tools`). If an admin hides the item via
  // Admin → Menu Directory, direct URL hits, bookmarks, and stale
  // breadcrumbs all redirect to /welcome instead of loading the
  // now-hidden surface.
  return (
    <AppShell>
      <MenuGate itemId="nav-ai-cpg-tools">
        <BrandProfileProvider>
          <Outlet />
        </BrandProfileProvider>
      </MenuGate>
    </AppShell>
  );
}

export function AILabRoutes({ guard: Guard } = {}) {
  const layout = <AILabLayout />;
  const element = Guard ? <Guard>{layout}</Guard> : layout;
  return (
    <Route path="/ai-cpg-tools" element={element}>
      <Route index element={<AILabDashboard />} />
      <Route path="prompt-builder" element={<PromptBuilder />} />
      <Route path="prompt-builder/:taskId" element={<PromptBuilder />} />
      <Route path="tools" element={<AIToolsDirectory />} />
      <Route path="brand-profile" element={<BrandProfilePage />} />
      <Route path="saved" element={<SavedPromptsPage />} />
      <Route path=":categorySlug" element={<ToolkitPage />} />
    </Route>
  );
}

export { AILabDashboard, PromptBuilder, ToolkitPage, AIToolsDirectory, BrandProfilePage, SavedPromptsPage, AILabLayout };
